/**
 * Leave-intent (exit) popup for WordPress (www). Ported almost verbatim from the
 * booking app's exit-popup.js so the offer behaves identically across www and
 * the booking app, sharing root-domain cookies and the same 5% discount.
 *
 * Behaviour:
 *  - Never runs inside an iframe (e.g. the WP header-only frame).
 *  - Cannot appear during the first cfg.bufferMs on the page (engagement buffer).
 *  - Shows at most once per browser session (session cookie).
 *  - Suppressed for 7 days after close / decline / CTA (persistent cookie).
 *  - Cookies use the root domain so they are shared with the booking app.
 *  - CTA redirects to the booking app with a hidden ?exit_offer=1 flag; booking
 *    applies the 5% discount automatically on landing.
 *  - Tracks: exit_popup_shown, exit_popup_cta_clicked, exit_popup_declined.
 *
 * WP-specific changes vs the booking app original:
 *  1. claim() redirects to cfg.bookingUrl instead of POSTing to a CSRF-protected
 *     claim endpoint (WordPress has no such endpoint).
 *  2. The mobile back-button trap only arms when the visitor arrived from outside
 *     our own site (external/direct referrer), so internal navigation is never
 *     hijacked. Inactivity + scroll-up remain armed everywhere.
 */
(function () {
  'use strict';

  // Never trigger inside an iframe (WP header-only frame, embeds, widgets).
  if (window !== window.parent) {
    return;
  }

  var cfg = window.exitPopupConfig;

  function log() {
    if (cfg && cfg.debug && window.console && window.console.log) {
      window.console.log.apply(window.console, ['[exit-popup]'].concat([].slice.call(arguments)));
    }
  }

  if (!cfg || !cfg.enabled) {
    log('disabled or no config', cfg);
    return;
  }

  function rootDomain() {
    var host = location.hostname;
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
      return null; // IP address - can't scope to a root domain
    }
    var parts = host.split('.');
    if (parts.length < 2) {
      return null; // e.g. "localhost"
    }
    return parts.slice(-2).join('.');
  }

  // True when the visitor navigated here from within our own root domain. Used to
  // suppress the back-button trap during internal navigation.
  function referrerIsInternal() {
    if (!document.referrer) {
      return false;
    }
    try {
      var refHost = new URL(document.referrer).hostname;
      var root = rootDomain();
      return !!root && (refHost === root || refHost.slice(-(root.length + 1)) === '.' + root);
    } catch (e) {
      return false;
    }
  }

  function getCookie(name) {
    var match = document.cookie.match('(^|;)\\s*' + name + '=([^;]+)');
    return match ? decodeURIComponent(match[2]) : null;
  }

  function setCookie(name, value, days) {
    var cookie = name + '=' + encodeURIComponent(value) + '; path=/';
    var root = rootDomain();
    if (root) {
      cookie += '; domain=.' + root;
    }
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      cookie += '; expires=' + date.toUTCString();
    }
    cookie += '; SameSite=Lax';
    if (location.protocol === 'https:') {
      cookie += '; Secure';
    }
    document.cookie = cookie;
  }

  function track(event) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: event });
    if (typeof logTracking === 'function') {
      try {
        logTracking(window.dataLayer);
      } catch (e) {}
    }
  }

  // Already dismissed (7-day suppression) or already shown this session.
  if (getCookie(cfg.dismissCookie) || getCookie(cfg.shownCookie)) {
    log('suppressed by cookie. dismissed=', getCookie(cfg.dismissCookie), 'shown=', getCookie(cfg.shownCookie));
    return;
  }

  function init() {
    var backdrop = document.querySelector('.exit-popup-backdrop');
    if (!backdrop) {
      log('init: no .exit-popup-backdrop element found');
      return;
    }
    log('init: ready, attaching triggers');

    var shown = false;
    var boundDesktop = false;
    var boundMobile = false;

    // Engagement buffer: no trigger may open the popup during the first moments
    // on the page. Triggers stay attached, so a later signal still works.
    var bufferMs = (typeof cfg.bufferMs === 'number' && cfg.bufferMs >= 0) ? cfg.bufferMs : 30000;
    var startedAt = Date.now();

    function bufferElapsed() {
      return (Date.now() - startedAt) >= bufferMs;
    }

    function isMobile() {
      return (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || window.innerWidth <= 768;
    }

    function show() {
      if (shown) {
        return;
      }
      if (!bufferElapsed()) {
        log('suppressed: within buffer', bufferMs - (Date.now() - startedAt), 'ms left');
        return;
      }
      shown = true;
      setCookie(cfg.shownCookie, '1', 0); // session cookie
      backdrop.classList.add('is-open');
      track('exit_popup_shown');
      log('shown');
      detachTriggers();
    }

    function dismiss() {
      backdrop.classList.remove('is-open');
      setCookie(cfg.dismissCookie, '1', cfg.dismissDays || 7);
      track('exit_popup_declined');
      log('declined/dismissed');
    }

    // On WordPress there is no CSRF-protected claim endpoint. Send the user to the
    // booking app with the hidden offer flag; booking applies the 5% discount
    // automatically on landing.
    function claim() {
      track('exit_popup_cta_clicked');
      setCookie(cfg.dismissCookie, '1', cfg.dismissDays || 7);
      log('claim: redirecting to', cfg.bookingUrl);
      window.location.href = cfg.bookingUrl;
    }

    // --- Desktop trigger: mouse leaves through the top of the viewport -------
    function onMouseOut(e) {
      if (!e.relatedTarget && !e.toElement && e.clientY <= 0) {
        show();
      }
    }

    // --- Mobile triggers -----------------------------------------------------
    // True exit-intent is unavailable on touch devices, so we approximate it
    // with proxy signals:
    //   1) inactivity timer (primary),
    //   2) hardened back-button trap (external/direct referrers only, primed on
    //      the first user gesture so the browser keeps the trap history entry).
    // The fast scroll-up proxy is intentionally disabled on WordPress
    // (cfg.scrollUpEnabled === false) as too aggressive for content pages; the
    // scroll listener is kept only to reset the inactivity timer.

    // (1) Inactivity timer.
    var idleTimer = null;
    var idleMs = (cfg.idleMs && cfg.idleMs > 0) ? cfg.idleMs : 30000;
    function onIdle() {
      // Keeps idle triggering working if the buffer is ever configured longer
      // than the idle window.
      if (!bufferElapsed()) {
        idleTimer = setTimeout(onIdle, bufferMs - (Date.now() - startedAt));
        return;
      }
      log('mobile: inactivity timeout reached');
      show();
    }
    function resetIdleTimer() {
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
      idleTimer = setTimeout(onIdle, idleMs);
    }

    // (2) Back-button trap. On Back, show the popup then re-push a state so a
    // subsequent Back leaves the page normally (fixes "immediately goes back").
    function onPopState() {
      log('mobile: popstate (back button)');
      show();
      try {
        history.pushState({ exitPopup: 1 }, '');
      } catch (e) {}
    }

    // The extra trap history entry must be added only AFTER a real user gesture.
    // Chromium's history-manipulation intervention discards pushState entries
    // created without user activation, so a trap pushed on page load gets
    // skipped and the first Back leaves the page. Prime once on first gesture.
    var backTrapPrimed = false;
    var primeEvents = ['touchstart', 'pointerdown', 'click', 'keydown', 'scroll'];
    function primeBackTrap() {
      if (backTrapPrimed || shown) {
        return;
      }
      backTrapPrimed = true;
      try {
        history.pushState({ exitPopup: 1 }, '');
      } catch (e) {}
      log('mobile: back-trap primed (user gesture)');
    }

    // Back-trap only when the visitor arrived from outside our own site, so we
    // never hijack Back during internal navigation.
    var backTrapArmed = false;
    var backTrapTimer = null;
    function armBackTrap() {
      if (backTrapArmed || shown) {
        return;
      }
      if (referrerIsInternal()) {
        log('mobile: skipping back-trap (internal referrer)');
        return;
      }
      backTrapArmed = true;
      log('mobile: arming back-trap (external/direct referrer)');
      window.addEventListener('popstate', onPopState);
      // Defer adding the trap entry until a user gesture (see primeBackTrap).
      primeEvents.forEach(function (evt) {
        window.addEventListener(evt, primeBackTrap, { once: true, passive: true, capture: true });
      });
    }

    // Arming during the buffer would swallow the first Back even though show()
    // refuses to open the popup, so wait out the remaining buffer first.
    function scheduleBackTrap() {
      if (backTrapArmed || backTrapTimer) {
        return;
      }
      var remaining = bufferMs - (Date.now() - startedAt);
      if (remaining <= 0) {
        armBackTrap();
        return;
      }
      backTrapTimer = setTimeout(function () {
        backTrapTimer = null;
        armBackTrap();
      }, remaining);
    }

    // (3) Fast scroll-up near the top of the page (config-driven thresholds).
    // Disabled on WordPress (cfg.scrollUpEnabled === false); scrolling still
    // counts as activity and resets the inactivity timer.
    var scrollUpEnabled = cfg.scrollUpEnabled !== false;
    var lastScrollY = window.pageYOffset;
    var reachedDepth = window.pageYOffset;
    var topRatio = (typeof cfg.scrollUpTopRatio === 'number') ? cfg.scrollUpTopRatio : 0.15;
    var minScrolled = (typeof cfg.scrollUpMinScrolled === 'number') ? cfg.scrollUpMinScrolled : 600;
    function onScroll() {
      resetIdleTimer();
      if (!scrollUpEnabled) {
        return;
      }
      var currentY = window.pageYOffset;
      if (currentY > reachedDepth) {
        reachedDepth = currentY;
      }
      var goingUp = currentY < lastScrollY;
      var docHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      var topZone = docHeight * topRatio;
      // Must have scrolled down a meaningful amount, then move back up into the
      // top zone of the document.
      if (goingUp && reachedDepth >= minScrolled && currentY <= topZone) {
        log('mobile: scroll-up into top zone', { currentY: currentY, reachedDepth: reachedDepth, topZone: topZone });
        show();
      }
      lastScrollY = currentY;
    }

    function attachTriggers() {
      if (isMobile()) {
        if (!boundMobile && cfg.hasActiveCart) {
          boundMobile = true;
          log('mobile: arming triggers');
          scheduleBackTrap();
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('touchstart', resetIdleTimer, { passive: true });
          window.addEventListener('click', resetIdleTimer, true);
          window.addEventListener('keydown', resetIdleTimer);
          resetIdleTimer();
        } else if (!boundMobile) {
          log('mobile: not arming (disabled by config)');
        }
      } else if (!boundDesktop) {
        boundDesktop = true;
        document.addEventListener('mouseout', onMouseOut);
      }
    }

    function detachTriggers() {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('popstate', onPopState);
      primeEvents.forEach(function (evt) {
        window.removeEventListener(evt, primeBackTrap, true);
      });
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', resetIdleTimer);
      window.removeEventListener('click', resetIdleTimer, true);
      window.removeEventListener('keydown', resetIdleTimer);
      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      if (backTrapTimer) {
        clearTimeout(backTrapTimer);
        backTrapTimer = null;
      }
    }

    // Wire close / decline / CTA.
    var closeBtn = backdrop.querySelector('.exit-popup-close');
    var declineBtn = backdrop.querySelector('.exit-popup-btn-secondary');
    var ctaBtn = backdrop.querySelector('.exit-popup-btn-primary');

    if (closeBtn) {
      closeBtn.addEventListener('click', dismiss);
    }
    if (declineBtn) {
      declineBtn.addEventListener('click', dismiss);
    }
    if (ctaBtn) {
      ctaBtn.addEventListener('click', claim);
    }
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) {
        dismiss();
      }
    });

    attachTriggers();
    // Re-evaluate desktop/mobile binding on resize (e.g. orientation change).
    window.addEventListener('resize', attachTriggers);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
